// The Bay Area: its rail systems and its biggest bus operators, their lines,
// and how route ids become the page's line ids. Shared by the page, the
// server's live feed and the network build (systems.ts has the rest, which
// only the server needs).
//
// BART comes from BART itself (api.bart.gov and bart.gov's static GTFS),
// everything else from 511.org (docs/bay-data.md).

import busLinesJson from './bus-lines.json' with { type: 'json' };

export const ADDITIONAL_BUS_OPERATORS = [
  ['tri-delta', '3D', 'Tri Delta Transit'],
  ['capitol-bus', 'AM', 'Capitol Corridor connecting buses'],
  ['county', 'CC', 'County Connection'],
  ['commute', 'CM', 'Commute.org shuttles'],
  ['santa-cruz', 'CR', 'Santa Cruz METRO'],
  ['dumbarton', 'DE', 'Dumbarton Express'],
  ['emery-express', 'EE', 'Emery Express'],
  ['emery', 'EM', 'Emery Go-Round'],
  ['fast', 'FS', 'FAST'],
  ['golden-gate', 'GG', 'Golden Gate Transit'],
  ['park', 'GP', 'Golden Gate Park Shuttle'],
  ['marin', 'MA', 'Marin Transit'],
  ['mission-bay', 'MB', 'Mission Bay shuttles'],
  ['mountain-view', 'MC', 'Mountain View Community Shuttle'],
  ['mvgo', 'MV', 'MVgo'],
  ['petaluma', 'PE', 'Petaluma Transit'],
  ['presidio', 'PG', 'Presidio Go'],
  ['rio-vista', 'RV', 'Rio Vista Delta Breeze'],
  ['solano', 'SE', 'Solano Transportation Authority'],
  ['airport', 'SI', 'SFO Airport shuttles'],
  ['links', 'SL', 'San Leandro LINKS'],
  ['sonoma', 'SO', 'Sonoma County Transit'],
  ['santa-rosa', 'SR', 'Santa Rosa CityBus'],
  ['south-sf', 'SS', 'South San Francisco shuttles'],
  ['soltrans', 'ST', 'SolTrans'],
  ['union-city', 'UC', 'Union City Transit'],
  ['vacaville', 'VC', 'Vacaville City Coach'],
  ['vine', 'VN', 'VINE Transit'],
  ['westcat', 'WC', 'WestCAT'],
  ['wheels', 'WH', 'Wheels'],
] as const;
export type AdditionalBusSystem = (typeof ADDITIONAL_BUS_OPERATORS)[number][0];
export type BusSystem = 'muni-bus' | 'ac' | 'vta-bus' | 'samtrans' | AdditionalBusSystem;
export type BaySystem = 'bart' | 'caltrain' | 'muni' | 'vta' | 'smart' | 'ace' | 'amtrak' | BusSystem;

// Each 511-sourced system's operator id: its static GTFS
// (datafeeds?operator_id=) and the prefix on its ids in the regional
// realtime feed ("SF:N"). Muni's and VTA's buses are in the same static GTFS
// and feed as their rail. BART isn't read from 511.
export const OPERATOR: Readonly<Record<Exclude<BaySystem, 'bart'>, string>> = {
  caltrain: 'CT',
  muni: 'SF',
  vta: 'SC',
  smart: 'SA',
  ace: 'CE',
  amtrak: 'AM',
  'muni-bus': 'SF',
  ac: 'AC',
  'vta-bus': 'SC',
  samtrans: 'SM',
  'tri-delta': '3D',
  county: 'CC',
  'capitol-bus': 'AM',
  commute: 'CM',
  'santa-cruz': 'CR',
  dumbarton: 'DE',
  'emery-express': 'EE',
  emery: 'EM',
  fast: 'FS',
  'golden-gate': 'GG',
  park: 'GP',
  marin: 'MA',
  'mission-bay': 'MB',
  'mountain-view': 'MC',
  mvgo: 'MV',
  petaluma: 'PE',
  presidio: 'PG',
  'rio-vista': 'RV',
  solano: 'SE',
  airport: 'SI',
  links: 'SL',
  sonoma: 'SO',
  'santa-rosa': 'SR',
  'south-sf': 'SS',
  soltrans: 'ST',
  'union-city': 'UC',
  vacaville: 'VC',
  vine: 'VN',
  westcat: 'WC',
  wheels: 'WH',
};

export const BUS_SYSTEMS: readonly BusSystem[] = [
  'muni-bus',
  'ac',
  'vta-bus',
  'samtrans',
  ...ADDITIONAL_BUS_OPERATORS.map(([id]) => id),
];
export const isBusSystem = (s: BaySystem): s is BusSystem => (BUS_SYSTEMS as readonly string[]).includes(s);

// The systems in the order the line list groups them, with their names.
export const BAY_SYSTEM_NAMES: readonly { readonly id: BaySystem; readonly name: string }[] = [
  { id: 'bart', name: 'BART' },
  { id: 'caltrain', name: 'Caltrain' },
  { id: 'muni', name: 'Muni' },
  { id: 'vta', name: 'VTA Light Rail' },
  { id: 'smart', name: 'SMART' },
  { id: 'ace', name: 'ACE' },
  { id: 'amtrak', name: 'Capitol Corridor' },
  // Buses: each its own folded group (@tol/core/minor).
  { id: 'muni-bus', name: 'Muni buses' },
  { id: 'ac', name: 'AC Transit' },
  { id: 'vta-bus', name: 'VTA buses' },
  { id: 'samtrans', name: 'SamTrans' },
  ...ADDITIONAL_BUS_OPERATORS.map(([id, , name]) => ({ id, name })),
];

// Route ids, per operator, to the page's lines. 511's regional feed prefixes
// them with the operator ("SF:N"), the operator's static GTFS doesn't ("N").
//   BART (its own GTFS): a number per colored line and direction, 1
//     Yellow-S and 2 Yellow-N (routes.txt, directions.txt); BB-A and BB-B,
//     bus bridges, aren't drawn.
//   Caltrain: its service patterns are routes, and the local is two (weekday
//     and weekend timetables).
//   Muni: the letters, and the cable cars' PH, PM and CA (Muni's map calls
//     the California line C); buses share the feed.
//   VTA: "Blue Line", and Event-Blue for the extra trains after games at
//     Levi's Stadium, which run on the Blue line's track.
// ---- buses

// One row of bus-lines.json: a bus route as its operator's routes.txt in
// 511's static GTFS has it (route_type 3), written by
// scripts/build-network.ts. Hundreds of routes that change with every
// timetable, so they aren't typed out here.
export type BusRow = readonly [system: BusSystem, routeId: string, code: string, name: string, color: string];

const isBusRow = (v: unknown): v is BusRow =>
  Array.isArray(v) &&
  v.length === 5 &&
  typeof v[0] === 'string' &&
  (BUS_SYSTEMS as readonly string[]).includes(v[0]) &&
  v.slice(1).every((x) => typeof x === 'string');
const isBusRows = (v: unknown): v is readonly BusRow[] => Array.isArray(v) && v.every((r) => isBusRow(r));
if (!isBusRows(busLinesJson)) throw new TypeError('bus-lines.json is not a table of bus routes');
const BUS_ROWS: readonly BusRow[] = busLinesJson;

// A bus route's line id: "ac-51A", "muni-bus-14R".
export const busLineId = (system: BusSystem, routeId: string): string => `${system}-${routeId}`;

const busRoutes = (system: BusSystem): Readonly<Record<string, string>> =>
  Object.fromEntries(BUS_ROWS.filter((r) => r[0] === system).map((r) => [r[1], busLineId(system, r[1])]));
const BUS_ROUTES: Readonly<Record<BusSystem, Readonly<Record<string, string>>>> = {
  'muni-bus': busRoutes('muni-bus'),
  ac: busRoutes('ac'),
  'vta-bus': busRoutes('vta-bus'),
  samtrans: busRoutes('samtrans'),
  'tri-delta': busRoutes('tri-delta'),
  county: busRoutes('county'),
  'capitol-bus': busRoutes('capitol-bus'),
  commute: busRoutes('commute'),
  'santa-cruz': busRoutes('santa-cruz'),
  dumbarton: busRoutes('dumbarton'),
  'emery-express': busRoutes('emery-express'),
  emery: busRoutes('emery'),
  fast: busRoutes('fast'),
  'golden-gate': busRoutes('golden-gate'),
  park: busRoutes('park'),
  marin: busRoutes('marin'),
  'mission-bay': busRoutes('mission-bay'),
  'mountain-view': busRoutes('mountain-view'),
  mvgo: busRoutes('mvgo'),
  petaluma: busRoutes('petaluma'),
  presidio: busRoutes('presidio'),
  'rio-vista': busRoutes('rio-vista'),
  solano: busRoutes('solano'),
  airport: busRoutes('airport'),
  links: busRoutes('links'),
  sonoma: busRoutes('sonoma'),
  'santa-rosa': busRoutes('santa-rosa'),
  'south-sf': busRoutes('south-sf'),
  soltrans: busRoutes('soltrans'),
  'union-city': busRoutes('union-city'),
  vacaville: busRoutes('vacaville'),
  vine: busRoutes('vine'),
  westcat: busRoutes('westcat'),
  wheels: busRoutes('wheels'),
};

const ROUTES: Readonly<Record<BaySystem, Readonly<Record<string, string>>>> = {
  bart: {
    '1': 'bart-yellow',
    '2': 'bart-yellow',
    '3': 'bart-orange',
    '4': 'bart-orange',
    '5': 'bart-green',
    '6': 'bart-green',
    '7': 'bart-red',
    '8': 'bart-red',
    '11': 'bart-blue',
    '12': 'bart-blue',
    '19': 'bart-grey',
    '20': 'bart-grey',
  },
  caltrain: {
    'Local Weekday': 'caltrain-local',
    'Local Weekend': 'caltrain-local',
    Limited: 'caltrain-limited',
    Express: 'caltrain-express',
    'South County': 'caltrain-south-county',
  },
  muni: Object.fromEntries(
    ['J', 'K', 'L', 'M', 'N', 'T', 'F', 'PH', 'PM', 'CA'].map((r) => [r, `muni-${r.toLowerCase()}`]),
  ),
  vta: Object.fromEntries(
    ['Blue', 'Green', 'Orange'].flatMap((c) => [
      [`${c} Line`, `vta-${c.toLowerCase()}`],
      [`Event-${c}`, `vta-${c.toLowerCase()}`],
    ]),
  ),
  smart: { SMART: 'smart' },
  ace: { ACE: 'ace' },
  amtrak: { CC: 'capitol-corridor' },
  ...BUS_ROUTES,
};

// A route id, prefixed by the regional feed or not, to the page's line id;
// '' for a route the page doesn't draw (another system's route, a bus route
// bus-lines.json doesn't know yet).
export function lineId(system: BaySystem, routeId: string): string {
  const colon = routeId.indexOf(':');
  if (colon >= 0 && (system === 'bart' || routeId.slice(0, colon) !== OPERATOR[system])) return '';
  return ROUTES[system][colon >= 0 ? routeId.slice(colon + 1) : routeId] ?? '';
}

export interface BayLine {
  readonly id: string;
  readonly system: BaySystem;
  // What the line list shows: "Yellow", "J".
  readonly name: string;
  // Cards and hover labels: "BART Yellow Line", "J Church".
  readonly label: string;
  // Official color.
  readonly color: string;
  // On-map color, brighter where the official one vanishes on the dark map.
  readonly show: string;
  // Drawn on the line's trains zoomed in: Muni's letters (the cable cars'
  // as on the Official Muni Map: PH, PM and C), and the buses' route numbers.
  readonly bullet?: string;
  // A bus: drawn only zoomed in (@tol/core/minor).
  readonly minor?: true;
}

type LineRow = readonly [id: string, system: BaySystem, name: string, label: string, color: string, show?: string];

// Colors are each operator's route_color in 511's static GTFS (September
// 2026), except Capitol Corridor's, which 511 leaves blank: that one is the
// line's color in Wikipedia's Amtrak route table (Module:Adjacent
// stations/Amtrak).
const LINE_ROWS: readonly LineRow[] = [
  ['bart-yellow', 'bart', 'Yellow', 'Yellow Line', '#FFFF33', '#F2EE3A'],
  ['bart-orange', 'bart', 'Orange', 'Orange Line', '#FF9933'],
  ['bart-green', 'bart', 'Green', 'Green Line', '#339933', '#3FB23F'],
  ['bart-red', 'bart', 'Red', 'Red Line', '#FF0000', '#FF2A2A'],
  ['bart-blue', 'bart', 'Blue', 'Blue Line', '#0099CC', '#12A8DD'],
  ['bart-grey', 'bart', 'Grey', 'Oakland Airport', '#B0BEC7'],
  ['caltrain-local', 'caltrain', 'Local', 'Caltrain Local', '#DCDDDE'],
  ['caltrain-limited', 'caltrain', 'Limited', 'Caltrain Limited', '#99D7DC'],
  ['caltrain-express', 'caltrain', 'Express', 'Caltrain Express', '#CE202F', '#E8333F'],
  ['caltrain-south-county', 'caltrain', 'South County', 'Caltrain South County Connector', '#FAE4A7'],
  ['muni-j', 'muni', 'J', 'J Church', '#A96614', '#C27A20'],
  ['muni-k', 'muni', 'K', 'K Ingleside', '#437C93', '#5494AD'],
  ['muni-l', 'muni', 'L', 'L Taraval', '#942D83', '#B13E9E'],
  ['muni-m', 'muni', 'M', 'M Ocean View', '#008547', '#00A558'],
  ['muni-n', 'muni', 'N', 'N Judah', '#005B95', '#2A7FC2'],
  ['muni-t', 'muni', 'T', 'T Third', '#BF2B45', '#D93A55'],
  ['muni-f', 'muni', 'F', 'F Market & Wharves', '#B49A36'],
  // The cable cars share the F's color in 511's GTFS.
  ['muni-ph', 'muni', 'PH', 'Powell–Hyde Cable Car', '#B49A36'],
  ['muni-pm', 'muni', 'PM', 'Powell–Mason Cable Car', '#B49A36'],
  ['muni-ca', 'muni', 'C', 'California Cable Car', '#B49A36'],
  ['vta-blue', 'vta', 'Blue', 'VTA Blue Line', '#007ACC', '#1C8FE0'],
  ['vta-green', 'vta', 'Green', 'VTA Green Line', '#379400', '#44AD0A'],
  ['vta-orange', 'vta', 'Orange', 'VTA Orange Line', '#CC6600', '#E07A12'],
  ['smart', 'smart', 'SMART', 'SMART (Larkspur–Windsor)', '#4D6D51', '#6F9A74'],
  ['ace', 'ace', 'ACE', 'Altamont Corridor Express', '#77297D', '#9C47A3'],
  ['capitol-corridor', 'amtrak', 'Capitol Corridor', 'Capitol Corridor', '#1C1B4D', '#6E7BD6'],
];

// The operators' names on a bus's card: "AC Transit 51A".
const BUS_LABEL: Readonly<Partial<Record<BusSystem, string>>> = {
  'muni-bus': 'Muni',
  ac: 'AC Transit',
  'vta-bus': 'VTA',
  samtrans: 'SamTrans',
};
// SamTrans leaves route_color blank in 511's GTFS; its buses are drawn in a
// neutral gray rather than a color it doesn't use.
const NO_COLOR = '#8A9199';

const RAIL_LINES: readonly BayLine[] = LINE_ROWS.map(([id, system, name, label, color, show]) => {
  const line: BayLine = { id, system, name, label, color, show: show ?? color };
  return system === 'muni' ? Object.assign(line, { bullet: name }) : line;
});

const BUS_LINES: readonly BayLine[] = BUS_ROWS.map(([system, routeId, code, name, color]) => {
  const c = color === '' ? NO_COLOR : `#${color.toUpperCase()}`;
  return {
    id: busLineId(system, routeId),
    system,
    name: name === '' ? code : `${code} ${name}`,
    label: `${BUS_LABEL[system] ?? ADDITIONAL_BUS_OPERATORS.find(([id]) => id === system)?.[2] ?? system} ${code}`,
    color: c,
    show: c,
    bullet: code,
    minor: true,
  };
});

export const BAY_LINES: readonly BayLine[] = [...RAIL_LINES, ...BUS_LINES];

export function linesOf(system: BaySystem, lines: readonly BayLine[] = BAY_LINES): ReadonlySet<string> {
  return new Set(lines.filter((l) => l.system === system).map((l) => l.id));
}

// Lines from a fresh bus catalog, so a build script can use one snapshot
// for its catalog and its network instead of the committed bus-lines.json.
export function bayLines(rows: readonly BusRow[]): readonly BayLine[] {
  return [
    ...RAIL_LINES,
    ...rows.map(([system, routeId, code, name, color]) => {
      const c = color === '' ? NO_COLOR : `#${color.toUpperCase()}`;
      return {
        id: busLineId(system, routeId),
        system,
        name: name === '' ? code : `${code} ${name}`,
        label: `${BUS_LABEL[system]} ${code}`,
        color: c,
        show: c,
        bullet: code,
        minor: true as const,
      };
    }),
  ];
}

// A route id to the page's line id from a fresh bus catalog.
export function lineIdFor(rows: readonly BusRow[]): (system: BaySystem, routeId: string) => string {
  const fresh: Record<string, Record<string, string>> = {};
  for (const system of BUS_SYSTEMS) fresh[system] = {};
  for (const [system, routeId] of rows) {
    const perSystem = fresh[system];
    if (perSystem !== undefined) perSystem[routeId] = busLineId(system, routeId);
  }
  const routes = { ...ROUTES, ...fresh } as Readonly<Record<BaySystem, Readonly<Record<string, string>>>>;
  return (system: BaySystem, routeId: string): string => {
    const colon = routeId.indexOf(':');
    if (colon >= 0 && (system === 'bart' || routeId.slice(0, colon) !== OPERATOR[system])) return '';
    return routes[system][colon >= 0 ? routeId.slice(colon + 1) : routeId] ?? '';
  };
}
